import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

import {
  type PaddleEventData,
  type CheckoutEventsData,
  type PricePreviewParams,
} from "@paddle/paddle-js";
import axios from "axios";
import dynamic from "next/dynamic";
import { useSession } from "next-auth/react";
import { useFeatureFlagVariantKey } from "posthog-js/react";

import { PriceId } from "@musicfy/contants/Subscriptions";
import {
  PAYWALL_EXPERIMENT_FLAG_KEY,
  PaywallReason,
  normalizePaywallVariant,
  originToReason,
} from "@musicfy/libs/analytics/paywall";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { useCheckoutProvider } from "@musicfy/libs/PaddleProvider/hooks/useCheckoutProvider";
import Checkout from "@musicfy/libs/PaddleProvider/screens/Checkout";
import MemorialDayPricing from "@musicfy/libs/PaddleProvider/screens/MemorialDayPricing";
import ProUpsell from "@musicfy/libs/PaddleProvider/screens/ProUpsell";
import StripeUpgrade from "@musicfy/libs/PaddleProvider/screens/StripeUpgrade";
import UpdatePayment from "@musicfy/libs/PaddleProvider/screens/UpdatePayment";
import Upgrade from "@musicfy/libs/PaddleProvider/screens/Upgrade";
import {
  type PaddleContext,
  type PaddleEventCallback,
  type PaddleCheckoutContext,
  type PaddleScreen,
  type PaddleUpdatePaymentContext,
  type UpdatePaymentEventData,
  PaddleScreens,
  type PaddlePricesContext,
  type PaddleUpgradePreviewContext,
  type PaddleTransactionsContext,
  type PricePreviewData,
  type StripeCheckoutContext,
  type StripeCheckoutState,
} from "@musicfy/libs/PaddleProvider/types";
import PaddleLoader from "@musicfy/scripts/PaddleLoader";
import {
  type PaddleTransaction,
  type PaddleSubscriptionUpdatePreview,
} from "@musicfy/types/paddle-server";
import { api } from "@musicfy/utils";
import { type StripeChangePreview } from "@musicfy/utils/stripePlanChange";

export const PADDLE_TARGET_FRAME = "paddle-container";

const StripeCheckout = dynamic(
  () => import("@musicfy/libs/PaddleProvider/screens/StripeCheckout"),
  { ssr: false }
);

const PADDLE_SCREENS: Record<PaddleScreen, React.ComponentType> = {
  [PaddleScreens.CHECKOUT]: Checkout,
  [PaddleScreens.UPDATE_PAYMENT]: UpdatePayment,
  [PaddleScreens.PRICING]: MemorialDayPricing,
  [PaddleScreens.UPGRADE]: Upgrade,
  [PaddleScreens.STRIPE_UPGRADE]: StripeUpgrade,
  [PaddleScreens.STRIPE_CHECKOUT]: StripeCheckout,
};

const Context = createContext<PaddleContext | undefined>(undefined);

export function usePaddleContext() {
  const paddleContext = useContext(Context);

  if (!paddleContext) {
    throw new Error("usePaddleContext must be used within a PaddleProvider");
  }

  return paddleContext;
}

const PaddleProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const { trackEvent } = useAnalytics();
  const { status } = useSession();
  const { checkoutProvider } = useCheckoutProvider();

  // Variant for the (future) dedicated paywall experiment. No flag with this
  // key exists yet, so this resolves to "control" until one is created; PostHog
  // separately auto-tags every event with the live `$feature/*` super-props
  // registered in usePricingExperiments.
  const paywallVariant = normalizePaywallVariant(
    useFeatureFlagVariantKey(PAYWALL_EXPERIMENT_FLAG_KEY)
  );
  // Hold the resolved variant in a ref so `startCheckoutFlow` keeps a stable
  // identity when the PostHog flag resolves. Without this, effects that depend
  // on the callback (e.g. pages/index.tsx `?show_pricing`) re-run and
  // double-fire analytics the moment the variant flips control → arm.
  const paywallVariantRef = useRef(paywallVariant);
  paywallVariantRef.current = paywallVariant;

  const [activePaddleScreen, setActivePaddleScreen] =
    useState<PaddleScreen | null>(null);

  const [origin, setOrigin] = useState<string | undefined>();
  const [isCheckoutUpdating, setIsCheckoutUpdating] = useState(false);
  const [isUpgradeUpdating, setIsUpgradeUpdating] = useState(false);

  const [checkoutData, setCheckoutData] = useState<CheckoutEventsData>();
  const [checkoutError, setCheckoutError] = useState<PaddleEventData>();
  const [pricesData, setPricesData] = useState<PricePreviewData>();
  const [upgradePreviewData, setUpgradePreviewData] =
    useState<PaddleSubscriptionUpdatePreview>();
  const [stripeChangePreview, setStripeChangePreview] =
    useState<StripeChangePreview>();
  const [transactions, setTransactions] = useState<PaddleTransaction[] | null>(
    null
  );

  const [stripeCheckout, setStripeCheckout] =
    useState<StripeCheckoutState | null>(null);
  const [isStripeCheckoutUpdating, setIsStripeCheckoutUpdating] =
    useState(false);
  const [isStripePaymentProcessing, setIsStripePaymentProcessing] =
    useState(false);
  const [canCloseStripeCheckout, setCanCloseStripeCheckout] = useState(true);
  const isFetchingStripeSubscriptionRef = useRef(false);

  const [updatePaymentData, setUpdatePaymentData] =
    useState<UpdatePaymentEventData>();
  const [updatePaymentError, setUpdatePaymentError] =
    useState<PaddleEventData>();

  const isFetchingSubscriptionRef = useRef(false);

  // Pro-Feature upsell interstitial: the origin of the wall that opened it.
  const [proUpsellOrigin, setProUpsellOrigin] = useState<string | null>(null);

  /** Non paddle frame related callbacks */
  const startCheckoutFlow = useCallback(
    (origin?: string) => {
      setActivePaddleScreen(PaddleScreens.PRICING);

      /* Analytics */
      setOrigin(origin);
      trackEvent("Pricing Page Viewed", {
        origin: origin,
        checkout_provider: checkoutProvider,
      });
      // Every upgrade wall opens the rail through here, so one seat covers them
      // all. Only fire "Paywall Shown" for a real feature wall — non-wall entry
      // points (promo banners, nav, account settings, email, checkout-recovery,
      // and paid-plan changes) resolve to `Other` and would otherwise inflate
      // the paywall funnel. `reason` is a pure, unit-tested map of `origin`.
      const reason = originToReason(origin);
      if (reason !== PaywallReason.Other) {
        trackEvent("Paywall Shown", {
          reason,
          variant: paywallVariantRef.current,
        });
      }
    },
    [trackEvent, checkoutProvider]
  );

  /**
   * Show the Pro-Feature upsell interstitial for a wall origin. The modal's CTA
   * proceeds to the real checkout via startCheckoutFlow(origin); dismissing it
   * just closes (no checkout). Callers already gated on !canUseFeature, so this
   * only ever opens for a user who can't use the feature.
   */
  const openProUpsell = useCallback((origin: string) => {
    setProUpsellOrigin(origin);
  }, []);

  /** Set up incoming paddle event listeners */
  const paddleEventCallbacksRef = useRef<PaddleEventCallback[]>([]);

  const subscribeToPaddleEvents = useCallback(
    (callback: PaddleEventCallback) => {
      paddleEventCallbacksRef.current.push(callback);
    },
    []
  );

  const unsubscribeFromPaddleEvents = useCallback(
    (callback: PaddleEventCallback) => {
      paddleEventCallbacksRef.current = paddleEventCallbacksRef.current.filter(
        (listener) => listener !== callback
      );
    },
    []
  );

  const paddleEventCallback = useCallback((event: PaddleEventData) => {
    paddleEventCallbacksRef.current.forEach((callback) => callback(event));
  }, []);

  const onPaddleSetup = useCallback(async () => {
    const items = Object.values(PriceId)
      .flatMap((priceId) => [{ priceId: priceId, quantity: 1 }])
      .filter((price) => {
        return !!price.priceId;
      });

    if (!window.Paddle) {
      return;
    }

    let ipAddress: string | undefined;

    try {
      const location = await axios.get<{ country: string; ip: string }>(
        "https://api.ipify.org?format=json"
      );

      ipAddress = location.data.ip;
    } catch (err) {
      console.error(err);
    }

    // Memorial Day prices are baked into Paddle directly — do NOT apply a
    // promo discount on top, or customers would be discounted twice.
    const requests: PricePreviewParams = {
      items: items,
      customerIpAddress: ipAddress,
    };

    try {
      const res = await window.Paddle.PricePreview(requests);
      setPricesData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  /** Set up checkout context */
  const checkoutContext: PaddleCheckoutContext = {
    checkoutData,
    setCheckoutData,
    checkoutError,
    setCheckoutError,
    isFetchingSubscriptionRef,
    isCheckoutUpdating,
    setIsCheckoutUpdating,
  };

  /** Set up update payments context */
  const updatePaymentContext: PaddleUpdatePaymentContext = {
    updatePaymentData,
    setUpdatePaymentData,
    updatePaymentError,
    setUpdatePaymentError,
  };

  /** Set up prices context */
  const pricesContext: PaddlePricesContext = {
    pricesData,
  };

  /** Set up upgrade preview context */
  const upgradePreviewContext: PaddleUpgradePreviewContext = {
    upgradePreviewData,
    setUpgradePreviewData,
    isUpgradeUpdating,
    setIsUpgradeUpdating,
    stripeChangePreview,
    setStripeChangePreview,
  };

  /** Set up transactions context for subscribed users */
  const getCustomerTransactions =
    api.subscription.getCustomerTransactions.useMutation({
      onSuccess: (data) => {
        setTransactions(data);
      },
      onError: () => {
        setTransactions([]);
      },
    });

  const transactionsContext: PaddleTransactionsContext = {
    transactions: transactions,
    isLoading: !transactions && getCustomerTransactions.isLoading,
    getCustomerTransactions: getCustomerTransactions,
  };

  const stripeCheckoutContext: StripeCheckoutContext = {
    stripeCheckout,
    setStripeCheckout,
    isStripeCheckoutUpdating,
    setIsStripeCheckoutUpdating,
    isStripePaymentProcessing,
    setIsStripePaymentProcessing,
    canCloseStripeCheckout,
    setCanCloseStripeCheckout,
    isFetchingStripeSubscriptionRef,
  };

  /** Set up paddle context */
  const paddleContextValue: PaddleContext = {
    checkoutContext,
    pricesContext,
    transactionsContext,
    updatePaymentContext,
    upgradePreviewContext,
    stripeCheckoutContext,
    subscribeToPaddleEvents,
    unsubscribeFromPaddleEvents,
    setActivePaddleScreen,
    startCheckoutFlow,
    openProUpsell,
    origin,
    setOrigin,
  };

  const PaddleScreen = activePaddleScreen
    ? PADDLE_SCREENS[activePaddleScreen]
    : null;

  return (
    <Context.Provider value={paddleContextValue}>
      {children}
      {status !== "loading" && (
        <PaddleLoader
          eventCallback={paddleEventCallback}
          onSetup={onPaddleSetup}
        />
      )}

      {!!PaddleScreen && <PaddleScreen />}

      {proUpsellOrigin !== null && (
        <ProUpsell
          reason={proUpsellOrigin}
          onDismiss={() => setProUpsellOrigin(null)}
          onUpgrade={() => {
            const origin = proUpsellOrigin;
            setProUpsellOrigin(null);
            startCheckoutFlow(origin);
          }}
        />
      )}
    </Context.Provider>
  );
};

export default PaddleProvider;
